'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Crown, Check, Lightning, Sparkle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useAuth, SignInButton } from '@clerk/nextjs';

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  trigger?: 'limit' | 'feature' | 'general';
  featureName?: string;
}

export function UpgradeModal({ open, onClose, trigger = 'general', featureName }: UpgradeModalProps) {
  const { isSignedIn } = useAuth();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('yearly');
  const [isLoading, setIsLoading] = useState(false);

  const handleUpgrade = async () => {
    setIsLoading(true);
    try {
      // Send plan + cycle, backend maps to actual price ID
      const response = await fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          plan: 'pro',
          cycle: billingCycle,
        }),
      });

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTitle = () => {
    switch (trigger) {
      case 'limit':
        return "You've hit your daily limit";
      case 'feature':
        return `Unlock ${featureName || 'this feature'}`;
      default:
        return 'Upgrade to Pro';
    }
  };

  const getDescription = () => {
    switch (trigger) {
      case 'limit':
        return "You've used all 5 free visuals for today. Upgrade to Pro for unlimited visuals!";
      case 'feature':
        return `${featureName || 'This feature'} is available on Pro. Upgrade to unlock it.`;
      default:
        return 'Get unlimited visuals, no watermarks, and priority speed.';
    }
  };

  const features = [
    'Unlimited visuals',
    'No watermark on exports',
    'All export formats (PNG, PDF, SVG)',
    'Unlimited history',
    'Priority rendering speed',
  ];

  const monthlyPrice = 9.99;
  const yearlyPrice = 79;
  const yearlySavings = Math.round((1 - (yearlyPrice / (monthlyPrice * 12))) * 100);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', duration: 0.5 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-background rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-muted transition-colors z-10"
            >
              <X weight="bold" className="size-5" />
            </button>

            {/* Header with gradient */}
            <div className="relative px-6 pt-8 pb-6 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-xl bg-primary text-primary-foreground">
                  <Crown weight="fill" className="size-6" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">{getTitle()}</h2>
                  <p className="text-sm text-muted-foreground">{getDescription()}</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 pb-6">
              {/* Billing toggle */}
              <div className="flex items-center justify-center gap-2 mb-6 p-1 bg-muted rounded-lg">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={cn(
                    'flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all',
                    billingCycle === 'monthly'
                      ? 'bg-background shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('yearly')}
                  className={cn(
                    'flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2',
                    billingCycle === 'yearly'
                      ? 'bg-background shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  Yearly
                  <Badge variant="secondary" className="text-xs bg-green-500/10 text-green-600">
                    -{yearlySavings}%
                  </Badge>
                </button>
              </div>

              {/* Price */}
              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold">
                    ${billingCycle === 'yearly' ? (yearlyPrice / 12).toFixed(2) : monthlyPrice.toFixed(2)}
                  </span>
                  <span className="text-muted-foreground">/month</span>
                </div>
                {billingCycle === 'yearly' && (
                  <p className="text-sm text-muted-foreground mt-1">
                    ${yearlyPrice} billed annually
                  </p>
                )}
              </div>

              {/* Features */}
              <div className="space-y-3 mb-6">
                {features.map((feature) => (
                  <div key={feature} className="flex items-center gap-3">
                    <div className="p-1 rounded-full bg-green-500/10">
                      <Check weight="bold" className="size-4 text-green-600" />
                    </div>
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              {isSignedIn ? (
                <Button
                  className="w-full h-12 text-base font-semibold"
                  onClick={handleUpgrade}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Sparkle className="size-5 animate-spin" />
                  ) : (
                    <>
                      <Lightning weight="fill" className="size-5 mr-2" />
                      Upgrade to Pro
                    </>
                  )}
                </Button>
              ) : (
                <SignInButton mode="modal">
                  <Button className="w-full h-12 text-base font-semibold">
                    <Lightning weight="fill" className="size-5 mr-2" />
                    Sign in to Upgrade
                  </Button>
                </SignInButton>
              )}

              <p className="text-xs text-center text-muted-foreground mt-4">
                Cancel anytime. No questions asked.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Hook for easy usage
export function useUpgradeModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [trigger, setTrigger] = useState<'limit' | 'feature' | 'general'>('general');
  const [featureName, setFeatureName] = useState<string | undefined>();

  const openModal = (options?: { trigger?: 'limit' | 'feature' | 'general'; featureName?: string }) => {
    setTrigger(options?.trigger || 'general');
    setFeatureName(options?.featureName);
    setIsOpen(true);
  };

  const closeModal = () => setIsOpen(false);

  return {
    isOpen,
    trigger,
    featureName,
    openModal,
    closeModal,
  };
}
